/**
 * Reading mode's one floating button (viewer/readingMode): a tap brings the song page's bars back
 * over the sheet; a drag moves it to any edge (it snaps there, lib/useFloatingDock), and so do its
 * arrow keys. Remembered per device. Reports what it covers so the sheet's chips keep clear of it.
 */
import { Menu } from 'lucide-react';
import { useEffect } from 'react';
import { useT } from '../i18n';
import { useFloatingDock, type DockInsets } from '../lib/useFloatingDock';

export const READING_BUTTON_DOCK = 'kgr.viewer.barsButton';

export function ReadingButton({ onOpen, onInsets }: { onOpen: () => void; onInsets?: (insets: DockInsets) => void }) {
  const t = useT();
  const floating = useFloatingDock<HTMLButtonElement>({ storageKey: READING_BUTTON_DOCK, fallback: { edge: 'top', along: 0 } });
  const { top, bottom, left, right } = floating.insets;
  useEffect(() => {
    onInsets?.({ top, bottom, left, right });
  }, [onInsets, top, bottom, left, right]);
  return (
    <button
      ref={floating.ref}
      type="button"
      aria-label={t('viewer.bars_show')}
      aria-haspopup="true"
      title={t('viewer.bars_button_hint')}
      data-dock={floating.dock.edge}
      {...floating.gripProps}
      onClick={() => {
        if (!floating.consumeDrag()) onOpen();
      }}
      className="pointer-events-auto absolute top-0 left-0 z-40 flex size-12 cursor-grab touch-none items-center justify-center rounded-full bg-[var(--surface)]/90 text-[var(--accent)] shadow-[0_6px_24px_rgb(0_0_0/0.28)] ring-1 ring-[var(--line)] backdrop-blur active:cursor-grabbing"
      style={floating.style}
    >
      <Menu className="size-6" aria-hidden />
    </button>
  );
}
